import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AttachmentUpload } from 'components/FormFields';

// Main container
const LogosContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Logo card
const LogoCard = styled.div`
  background: ${({ theme }) => theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
`;

const LogoHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const LogoIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const LogoInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LogoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px 0;
`;

const LogoDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0;
  line-height: 1.5;
`;

// Icons
const ImageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const WristbandIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
    <path d="M12 18c3.314 0 6-2.686 6-6s-2.686-6-6-6-6 2.686-6 6 2.686 6 6 6z" />
    <path d="M12 14c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" />
  </svg>
);

const LogosTab = ({
  logo = null,
  logoId = null,
  kioskLogo = null,
  kioskLogoId = null,
  setFieldValue,
}) => {
  const [logoAttachments, setLogoAttachments] = useState([]);
  const [kioskAttachments, setKioskAttachments] = useState([]);

  // Initialize attachments from existing logos
  useEffect(() => {
    if (logo && !logoAttachments.length) {
      setLogoAttachments([
        {
          id: logoId || 'existing-logo',
          name: 'Account Logo',
          size: 0,
          sizeFormatted: '',
          url: logo,
          type: 'image/jpeg',
          isImage: true,
        },
      ]);
    }
  }, [logo, logoId, logoAttachments.length]);

  useEffect(() => {
    if (kioskLogo && !kioskAttachments.length) {
      setKioskAttachments([
        {
          id: kioskLogoId || 'existing-kiosk-logo',
          name: 'Kiosk Armband Logo',
          size: 0,
          sizeFormatted: '',
          url: kioskLogo,
          type: 'image/jpeg',
          isImage: true,
        },
      ]);
    }
  }, [kioskLogo, kioskLogoId, kioskAttachments.length]);

  const handleLogoChange = (attachments) => {
    setLogoAttachments(attachments);
    if (attachments.length > 0) {
      setFieldValue('logo', attachments[0].url);
      setFieldValue('logo_id', attachments[0].id);
    } else {
      setFieldValue('logo', null);
      setFieldValue('logo_id', null);
    }
  };

  const handleKioskLogoChange = (attachments) => {
    setKioskAttachments(attachments);
    if (attachments.length > 0) {
      setFieldValue('kiosk_logo', attachments[0].url);
      setFieldValue('kiosk_logo_id', attachments[0].id);
    } else {
      setFieldValue('kiosk_logo', null);
      setFieldValue('kiosk_logo_id', null);
    }
  };

  return (
    <LogosContainer>
      {/* Account Logo */}
      <LogoCard>
        <LogoHeader>
          <LogoIcon>
            <ImageIcon />
          </LogoIcon>
          <LogoInfo>
            <LogoTitle>Account Logo</LogoTitle>
            <LogoDescription>
              Used for all external customer communications including emails,
              tickets, and receipts.
            </LogoDescription>
          </LogoInfo>
        </LogoHeader>

        <AttachmentUpload
          attachments={logoAttachments}
          onAttachmentsChange={handleLogoChange}
          maxFiles={1}
          maxFileSize={5242880}
          defaultExpanded={!!logo}
        />
      </LogoCard>

      {/* Kiosk Armband Logo */}
      <LogoCard>
        <LogoHeader>
          <LogoIcon>
            <WristbandIcon />
          </LogoIcon>
          <LogoInfo>
            <LogoTitle>Kiosk Armband Logo</LogoTitle>
            <LogoDescription>
              Used when printing armbands from kiosks. This logo will appear on
              the printed armband.
            </LogoDescription>
          </LogoInfo>
        </LogoHeader>

        <AttachmentUpload
          attachments={kioskAttachments}
          onAttachmentsChange={handleKioskLogoChange}
          maxFiles={1}
          maxFileSize={5242880}
          defaultExpanded={!!kioskLogo}
        />
      </LogoCard>
    </LogosContainer>
  );
};

LogosTab.propTypes = {
  logo: PropTypes.string,
  logoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  kioskLogo: PropTypes.string,
  kioskLogoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setFieldValue: PropTypes.func.isRequired,
};

export default LogosTab;
